import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    ReferenceLine,
    DefaultLegendContent,
} from "recharts";

export type MonthlySalesProfitYoYRow = {
    month: string; // "YYYY/MM"
    sales: number;
    marginalProfit: number;
    operatingProfit: number;

    // YoY（例: 0.12 = +12%）
    salesYoy?: number | null;
    marginalProfitYoy?: number | null;
    operatingProfitYoy?: number | null;
};

const LEGEND_ORDER = [
    "sales",
    "marginalProfit",
    "operatingProfit",
    "salesYoy",
    "marginalProfitYoy",
    "operatingProfitYoy",
];

const renderSortedLegend = ({ payload }: any) => {
    if (!payload) return null;

    const sorted = [...payload].sort((a: any, b: any) => {
        const ai = LEGEND_ORDER.indexOf(a.dataKey);
        const bi = LEGEND_ORDER.indexOf(b.dataKey);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    // ✅ Rechartsのデフォルトに近い見た目（ul/li + 小さい四角）
    return (
        <ul style={{ padding: 0, margin: 0 }}>
            {sorted.map((e: any, idx: number) => (
                <li
                    key={`${e.dataKey}-${idx}`}
                    style={{ display: "inline-block", marginRight: 10 }}
                >
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 32 32"
                        style={{ display: "inline-block", verticalAlign: "middle", marginRight: 4 }}
                    >
                        <path fill={e.color} d="M0,0h32v32H0z" />
                    </svg>
                    <span style={{ verticalAlign: "middle" }}>{e.value}</span>
                </li>
            ))}
        </ul>
    );
};


type Props = {
    data: MonthlySalesProfitYoYRow[];
    loading?: boolean;
    emptyLabel?: string;
    title?: string;
};

function fmtCompactYen(v: number) {
    const n = Number(v || 0);
    const a = Math.abs(n);
    if (a >= 1e8) return `${(n / 1e8).toFixed(1)}億`;
    if (a >= 1e4) return `${(n / 1e4).toFixed(1)}万`;
    return `${Math.round(n).toLocaleString("ja-JP")}`;
}

function fmtYen(v: number) {
    return `${Math.round(Number(v || 0)).toLocaleString("ja-JP")}円`;
}

function fmtPct(v: number) {
    if (v == null || !isFinite(v)) return "—";
    const p = v * 100;
    const sign = p > 0 ? "+" : "";
    return `${sign}${p.toFixed(1)}%`;
}

export default function MonthlySalesProfitYoYChart({
    data,
    loading = false,
    emptyLabel = "データがありません",
    title = "売上・限界利益・営業利益 推移（昨対比%）",
}: Props) {
    const hasData = Array.isArray(data) && data.length > 0;

    // 右軸（%）の表示レンジをざっくり自動調整（見切れ対策）
    const yoyDomain = useMemo(() => {
        const vals = (data || [])
            .flatMap((r) => [r.salesYoy, r.marginalProfitYoy, r.operatingProfitYoy])
            .filter((v): v is number => typeof v === "number" && isFinite(v));

        if (!vals.length) return ["auto", "auto"] as const;

        const min = Math.min(...vals);
        const max = Math.max(...vals);

        // 余白を少し足す
        const pad = 0.15;
        const lo = Math.min(min - pad, -1.5); // 下は最低 -150% まで見せる
        const hi = Math.max(max + pad, 0.5);  // 上は最低 +50% まで見せる
        return [lo, hi] as const;
    }, [data]);

    return (
        <div className="rounded-2xl border bg-white p-4">
            <div className="mb-3 text-xs font-semibold text-slate-800">{title}</div>

            {!hasData ? (
                <div className="text-sm text-slate-500">
                    {loading ? "読み込み中..." : emptyLabel}
                </div>
            ) : (
                <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                        <ComposedChart
                            data={data}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            barCategoryGap={14}
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="month"
                                tickMargin={6}
                                tick={{ fontSize: 11 }}
                            />

                            <YAxis
                                yAxisId="left"
                                tickFormatter={(v) => fmtCompactYen(Number(v))}
                                width={56}
                                tick={{ fontSize: 11 }}
                            />

                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tickFormatter={(v) => fmtPct(Number(v))}
                                domain={yoyDomain as any}
                                width={64}
                                tick={{ fontSize: 11 }}
                            />

                            <ReferenceLine yAxisId="right" y={0} strokeDasharray="4 4" />

                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload || !payload.length) return null;
                                    const p: any = payload.reduce((acc: any, cur: any) => {
                                        acc[cur.dataKey] = cur.value;
                                        return acc;
                                    }, {});
                                    return (
                                        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow">
                                            <div className="mb-1 font-semibold">{label}</div>
                                            <div className="space-y-1 text-slate-700">
                                                <div>売上: {fmtYen(p.sales)}</div>
                                                <div>限界利益: {fmtYen(p.marginalProfit)}</div>
                                                <div>営業利益: {fmtYen(p.operatingProfit)}</div>

                                                <div className="pt-1 border-t text-slate-600">
                                                    売上 昨対: {fmtPct(p.salesYoy)}
                                                    <br />
                                                    限界利益 昨対: {fmtPct(p.marginalProfitYoy)}
                                                    <br />
                                                    営業利益 昨対: {fmtPct(p.operatingProfitYoy)}
                                                </div>

                                            </div>
                                        </div>
                                    );
                                }}
                            />







                            {/* 今期：棒（青ベースで3段階） */}
                            <Bar yAxisId="left" dataKey="sales" name="売上" fill="#1D4ED8" barSize={16} />
                            <Bar yAxisId="left" dataKey="marginalProfit" name="限界利益" fill="#3B82F6" barSize={16} />
                            <Bar yAxisId="left" dataKey="operatingProfit" name="営業利益" fill="#93C5FD" barSize={16} />


                            {/* 昨対：折れ線（青ベースで濃淡） */}

                            <Line
                                yAxisId="right"
                                type="linear"
                                dataKey="salesYoy"
                                name="売上 昨対(%)"
                                stroke="#1E3A8A"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                            />
                            <Line
                                yAxisId="right"
                                type="linear"
                                dataKey="marginalProfitYoy"
                                name="限界利益 昨対(%)"
                                stroke="#2563EB"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                            />
                            <Line
                                yAxisId="right"
                                type="linear"
                                dataKey="operatingProfitYoy"
                                name="営業利益 昨対(%)"
                                stroke="#60A5FA"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                            />

                            <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                content={(props: any) => {
                                    const payload = props?.payload || [];
                                    const sorted = [...payload].sort((a: any, b: any) => {
                                        const ai = LEGEND_ORDER.indexOf(a.dataKey);
                                        const bi = LEGEND_ORDER.indexOf(b.dataKey);
                                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                    });

                                    // ✅ 描画は Recharts 標準（アイコンも標準のまま）
                                    return <DefaultLegendContent {...props} payload={sorted} />;
                                }}
                            />


                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}